/**
 * Feriados nacionais.
 *
 * Versionados no repositório, nunca buscados em API (RN-138): o sistema roda offline, de
 * uma pasta local, e um calendário que depende de rede é um calendário que falha no
 * canteiro justamente quando alguém precisa dele.
 *
 * Os móveis derivam da Páscoa, que se calcula — não se tabela.
 */

export interface Holiday {
  readonly date: string;
  readonly name: string;
  /**
   * Feriado nacional por lei, ou ponto facultativo?
   *
   * Carnaval e Corpus Christi param obra no Brasil inteiro, mas a União não os declara
   * feriado — são ponto facultativo. Quem decide se a obra para é quem administra, e a
   * importação mostra a diferença em vez de escondê-la.
   */
  readonly mandatory: boolean;
}

/** Feriados de data fixa, no formato `MM-DD`. Todos obrigatórios por lei federal. */
const FIXED: readonly { monthDay: string; name: string }[] = [
  { monthDay: '01-01', name: 'Confraternização Universal' },
  { monthDay: '04-21', name: 'Tiradentes' },
  { monthDay: '05-01', name: 'Dia do Trabalho' },
  { monthDay: '09-07', name: 'Independência' },
  { monthDay: '10-12', name: 'Nossa Senhora Aparecida' },
  { monthDay: '11-02', name: 'Finados' },
  { monthDay: '11-15', name: 'Proclamação da República' },
  { monthDay: '11-20', name: 'Consciência Negra' },
  { monthDay: '12-25', name: 'Natal' },
];

/**
 * Domingo de Páscoa pelo algoritmo de Meeus/Jones/Butcher.
 *
 * Aritmética inteira pura, em UTC: nenhuma data do sistema passa por fuso (RN-120).
 */
function easterSunday(year: number): Date {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;

  return new Date(Date.UTC(year, month - 1, day));
}

function toIso(date: Date): string {
  return `${String(date.getUTCFullYear()).padStart(4, '0')}-${String(date.getUTCMonth() + 1).padStart(2, '0')}-${String(date.getUTCDate()).padStart(2, '0')}`;
}

function shift(from: Date, days: number): Date {
  return new Date(from.getTime() + days * 86_400_000);
}

/**
 * Feriados nacionais do ano.
 *
 * Carnaval e Corpus Christi entram porque param obra no Brasil inteiro, marcados como
 * facultativos. Feriado municipal não está aqui: é exceção cadastrada à mão, e é para
 * isso que `WorkCalendarException` existe.
 */
export function nationalHolidays(year: number): Holiday[] {
  const easter = easterSunday(year);

  const movable: Holiday[] = [
    { date: toIso(shift(easter, -48)), name: 'Carnaval (segunda)', mandatory: false },
    { date: toIso(shift(easter, -47)), name: 'Carnaval', mandatory: false },
    { date: toIso(shift(easter, -2)), name: 'Sexta-feira Santa', mandatory: true },
    { date: toIso(shift(easter, 60)), name: 'Corpus Christi', mandatory: false },
  ];

  const fixed = FIXED.map((holiday) => ({
    date: `${String(year)}-${holiday.monthDay}`,
    name: holiday.name,
    mandatory: true,
  }));

  return [...fixed, ...movable].sort((a, b) => a.date.localeCompare(b.date));
}

/** Os feriados de um intervalo de anos — o que o seed do calendário usa. */
export function holidaysBetween(fromYear: number, toYear: number): Holiday[] {
  const years = Array.from({ length: toYear - fromYear + 1 }, (_, index) => fromYear + index);
  return years.flatMap(nationalHolidays);
}
